import React, { useEffect, useMemo, useState } from 'react';

interface PostSlot {
    id: number;
    channel: 'facebook' | 'instagram';
    format: string | null;
    scheduled_at: string | null;
    published_at: string | null;
    hook: string | null;
    status: string;
    external_post_id: string | null;
    permalink: string | null;
    media_urls: string | null;
    media_status: string | null;
    media_error: string | null;
    error_log: string | null;
    created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
    published: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    failed: 'bg-red-500/10 text-red-600 dark:text-red-400',
    rendering: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    approved: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    draft: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
};

function firstMediaUrl(media_urls: string | null): string | null {
    if (!media_urls) return null;
    try {
        const arr = JSON.parse(media_urls);
        return Array.isArray(arr) && arr.length > 0 ? arr[0] : null;
    } catch { return null; }
}

export const PostyFbIgPage: React.FC = () => {
    const [items, setItems] = useState<PostSlot[]>([]);
    const [failed24h, setFailed24h] = useState(0);
    const [stuck, setStuck] = useState(0);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [filterChannel, setFilterChannel] = useState<string>('');
    const [filterStatus, setFilterStatus] = useState<string>('');

    function refresh() {
        setLoading(true);
        const params = new URLSearchParams();
        if (filterChannel) params.set('channel', filterChannel);
        params.set('days', '14');
        params.set('limit', '100');
        fetch(`/api/admin/content/published?${params.toString()}`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
            .then(d => {
                setItems(d.items || []);
                setFailed24h(d.failed_24h || 0);
                setStuck(d.stuck_rendering || 0);
            })
            .catch(e => setErr(String(e)))
            .finally(() => setLoading(false));
    }

    useEffect(() => { refresh(); }, [filterChannel]);
    useEffect(() => {
        const t = setInterval(refresh, 60000);
        return () => clearInterval(t);
    }, []);

    const filtered = useMemo(() => {
        if (!filterStatus) return items;
        return items.filter(i => i.status === filterStatus);
    }, [items, filterStatus]);

    return (
        <div>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h1 className="text-2xl font-bold">Posty FB + IG</h1>
                <div className="flex gap-2 items-center text-xs">
                    {failed24h > 0 && (
                        <span className="px-2 py-1 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 font-semibold">
                            ❌ Failed (24h): {failed24h}
                        </span>
                    )}
                    {stuck > 0 && (
                        <span className="px-2 py-1 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 font-semibold">
                            ⚠️ Stuck rendering: {stuck}
                        </span>
                    )}
                    <button onClick={refresh} className="px-3 py-1.5 bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 rounded-lg font-semibold">
                        🔄 Odśwież
                    </button>
                </div>
            </div>

            <div className="flex gap-3 mb-4 flex-wrap items-center">
                <select
                    value={filterChannel}
                    onChange={e => setFilterChannel(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-border-light dark:border-border-dark rounded-lg bg-bkg-light dark:bg-bkg-dark"
                >
                    <option value="">Wszystkie kanały</option>
                    <option value="facebook">📘 Facebook</option>
                    <option value="instagram">📷 Instagram</option>
                </select>
                <select
                    value={filterStatus}
                    onChange={e => setFilterStatus(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-border-light dark:border-border-dark rounded-lg bg-bkg-light dark:bg-bkg-dark"
                >
                    <option value="">Wszystkie statusy</option>
                    <option value="published">✅ Published</option>
                    <option value="failed">❌ Failed</option>
                    <option value="rendering">⏳ Rendering</option>
                    <option value="approved">🔵 Approved</option>
                    <option value="draft">⚪ Draft</option>
                </select>
                <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark ml-auto">
                    Pokazano: <b>{filtered.length}</b> / {items.length} · ostatnie 14 dni · auto-odświeżanie 60s
                </span>
            </div>

            {err && <div className="text-red-500 text-sm mb-4">{err}</div>}
            {loading && items.length === 0 && <div className="text-text-secondary-light dark:text-text-secondary-dark">Ładowanie…</div>}

            <div className="space-y-2">
                {filtered.map(slot => {
                    const thumb = firstMediaUrl(slot.media_urls);
                    const statusClass = STATUS_COLORS[slot.status] || STATUS_COLORS.draft;
                    return (
                        <div key={slot.id} className="bg-white dark:bg-content-dark rounded-xl border border-border-light dark:border-border-dark p-3 flex gap-3 items-start">
                            {/* Thumb */}
                            <div className="w-20 h-20 rounded-lg bg-bkg-light dark:bg-bkg-dark flex-shrink-0 overflow-hidden flex items-center justify-center">
                                {thumb ? (
                                    <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                                ) : (
                                    <span className="text-2xl opacity-30">🖼️</span>
                                )}
                            </div>

                            {/* Body */}
                            <div className="flex-grow min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="text-base">{slot.channel === 'facebook' ? '📘' : '📷'}</span>
                                    <span className="font-mono text-xs text-text-secondary-light dark:text-text-secondary-dark">#{slot.id}</span>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusClass}`}>
                                        {slot.status}
                                    </span>
                                    {slot.format && slot.format !== 'post' && (
                                        <span className="px-2 py-0.5 rounded-full text-[10px] bg-violet-500/10 text-violet-600 dark:text-violet-400">
                                            {slot.format}
                                        </span>
                                    )}
                                    <span className="text-[10px] text-text-secondary-light dark:text-text-secondary-dark ml-auto">
                                        {slot.published_at?.slice(5, 16) || slot.scheduled_at?.slice(5, 16) || slot.created_at?.slice(5, 16)}
                                    </span>
                                </div>
                                <div className="text-sm font-medium mb-1 truncate">{slot.hook || '—'}</div>
                                {slot.media_error && (
                                    <div className="text-[11px] text-red-600 dark:text-red-400 truncate">
                                        ⚠️ Media: {slot.media_error}
                                    </div>
                                )}
                                {slot.error_log && (
                                    <div className="text-[11px] text-red-600 dark:text-red-400 truncate">
                                        ⚠️ Error: {slot.error_log}
                                    </div>
                                )}
                                <div className="flex gap-3 mt-1 text-xs">
                                    {slot.permalink && (
                                        <a href={slot.permalink} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
                                            Otwórz post →
                                        </a>
                                    )}
                                    {slot.external_post_id && !slot.permalink && (
                                        <span className="text-text-secondary-light dark:text-text-secondary-dark font-mono">
                                            {slot.external_post_id.slice(0, 40)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
                {filtered.length === 0 && !loading && (
                    <div className="text-center py-12 text-text-secondary-light dark:text-text-secondary-dark text-sm">
                        Brak postów w ostatnich 14 dniach z tymi filtrami.
                    </div>
                )}
            </div>
        </div>
    );
};
